import { Alert, Box, Grid2, ListItem, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useFormikContext } from 'formik';

import tid from '@/common/infrastructure/config/tid';
import FormikTypeahead from '@/common/presentation/components/forms/fields/FormikTypeahead';
import { TypeaheadOption } from '@/common/presentation/components/inputs/Typeahead/index';
import { JobFormData } from '@/jobs/application/types/JobTypes';
import { organizationTimezoneService } from '@/common/infrastructure/services/OrganizationTimezoneService';
import { BACKGROUND_COLORS, TEXT_COLORS } from '@/common/presentation/constants/colors';

interface JobTimezoneFieldProps {
  userTimezone: TypeaheadOption<string> | null;
  timezoneError: { message: string } | null;
  timezoneLoading: boolean;
  clearTimezoneError: () => void;
  fetchTimeZonesWithInitial: (searchTerm: string) => Promise<TypeaheadOption<string>[]>;
  initialTimeZone?: TypeaheadOption<string> | null;
  handleTimezoneSelection?: (selectedTimezone: TypeaheadOption<string> | null) => void;
  showHelperText?: boolean;
  showError?: boolean;
}

export const JobTimezoneField = ({
  userTimezone,
  initialTimeZone,
  timezoneError,
  timezoneLoading,
  clearTimezoneError,
  fetchTimeZonesWithInitial,
  handleTimezoneSelection,
  showHelperText = false,
  showError = true
}: JobTimezoneFieldProps) => {
  const { setFieldValue } = useFormikContext<JobFormData>();
  // Track if the user has manually cleared the field to prevent initialOption from being re-applied
  const [hasBeenCleared, setHasBeenCleared] = useState(false);
  // Use a counter for the key to force remount only when clearing
  const [clearKey, setClearKey] = useState(0);

  // Reset the cleared flag when initialTimeZone changes (e.g., when loading a new job)
  useEffect(() => {
    if (initialTimeZone) {
      setHasBeenCleared(false);
      // Remove the explicit clear flag when a new initial timezone is provided
      localStorage.removeItem('timezoneExplicitlyCleared');
    }
  }, [initialTimeZone]);

  // Function to parse display text to match renderOption format
  // Memoized with useCallback to ensure stable reference and prevent React hooks warnings
  const parseTimezoneDisplayText = useCallback((option: TypeaheadOption<string>): string => {
    // Keep the UTC part in the display text to match renderOption format
    // Example: "(UTC-05:00) Eastern Time (US & Canada)" -> "(UTC-05:00) Eastern Time (US & Canada)"
    return option.displayText || option.label;
  }, []);

  // Custom handler that stores timezone in localStorage
  const handleTimezoneChangeWithStorage = (selectedTimezone: TypeaheadOption<string> | null) => {
    if (selectedTimezone?.value) {
      // Store in localStorage when user selects a timezone
      organizationTimezoneService.storeOrganizationDefaultTimezone(selectedTimezone.value);
      // Set flag to prevent AuthGuard from overriding user selection
      localStorage.setItem('userTimezoneManuallySelected', 'true');
      // Remove the explicit clear flag
      localStorage.removeItem('timezoneExplicitlyCleared');
      // Reset the cleared flag when user selects a new timezone
      setHasBeenCleared(false);
      // Call the original handler when selecting
      if (handleTimezoneSelection) {
        handleTimezoneSelection(selectedTimezone);
      }
    } else {
      // Clear localStorage when user clears the timezone
      localStorage.removeItem('userTimezoneManuallySelected');
      // Set a flag to indicate the user explicitly cleared the field
      // This prevents the hierarchy hook from re-applying a timezone
      localStorage.setItem('timezoneExplicitlyCleared', 'true');
      // Also clear the stored organization default timezone to prevent it from being re-applied
      organizationTimezoneService.clearOrganizationDefaultTimezone();
      // Mark that the field has been manually cleared and force remount
      setHasBeenCleared(true);
      setClearKey((prev) => prev + 1);
      // Explicitly set the field value to null to ensure it's cleared
      setFieldValue('timeZone', null);
      // Call the original handler - it now respects the explicit clear flag
      if (handleTimezoneSelection) {
        handleTimezoneSelection(selectedTimezone);
      }
    }
  };

  // Always use the original FormikTypeahead implementation
  return (
    <>
      {showError && timezoneError && (
        <Grid2 size={12} sx={{ mb: 2 }}>
          <Alert severity="error" onClose={clearTimezoneError} sx={{ mb: 2 }}>
            <Typography variant="body2">{timezoneError.message}</Typography>
          </Alert>
        </Grid2>
      )}
      <Grid2 size={12}>
        <Tooltip title="Select the time zone where the job will be performed" arrow placement="top">
          <span>
            <FormikTypeahead<string, JobFormData>
              key={`timezone-${clearKey}`}
              id="timeZone"
              {...tid('jobs:details:time-zone-select')}
              fetchData={fetchTimeZonesWithInitial}
              minSearchLength={0}
              placeholder="Job Time Zone"
              noOptionsText="No timezones found"
              initialOption={hasBeenCleared ? undefined : initialTimeZone || undefined}
              loading={timezoneLoading}
              loadInitialData={true}
              clearOnBlur={false}
              disableClearable={false}
              onChange={handleTimezoneChangeWithStorage}
              parseDisplayText={parseTimezoneDisplayText}
              renderOption={(props: React.HTMLAttributes<HTMLLIElement> & { key?: string }, option) => {
                const isUserTimezone = userTimezone?.data === option.data;
                const firstSpaceIndex = option.label.indexOf(' '); // label example: "(UTC-05:00) Eastern Time (US & Canada)"
                const utcTime = option.label.slice(0, firstSpaceIndex); // (UTC-05:00)
                const timezoneLabel = option.label.slice(firstSpaceIndex + 1); // Eastern Time (US & Canada)
                return (
                  <ListItem
                    {...props}
                    key={props.key}
                    style={{ minHeight: '48px', backgroundColor: isUserTimezone ? BACKGROUND_COLORS.USER_TIMEZONE : undefined }}
                  >
                    <Box>
                      <Box component="span" sx={{ color: TEXT_COLORS.GREY_600 }}>
                        {utcTime}
                      </Box>
                      &nbsp;{timezoneLabel}
                    </Box>
                  </ListItem>
                );
              }}
              slotProps={{
                listbox: {
                  style: {
                    maxHeight: 240 // 48 x 5, each form options has 48px height
                  }
                }
              }}
            />
          </span>
        </Tooltip>
      </Grid2>
      {showHelperText && (
        <Grid2 size={12}>
          <Typography variant="caption" display="block" sx={{ mb: 2, lineHeight: 1.66, color: TEXT_COLORS.TEXT_SECONDARY }}>
            Use the time zone where the job will take place, regardless of the client or dispatch location.
          </Typography>
        </Grid2>
      )}
    </>
  );
};
